// src/pages/assessments/AssessmentListPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";
import { useBreadcrumb } from "../../context/BreadcrumbContext";

import {
  getAssessmentTypes,
  getAssessmentParentDetails,
  getAssessmentsPage,
  deleteAssessment,
} from "../../api/assessmentApi";
import { getPropertyById } from "../../api/unitApi";
import { formatDateUk } from "../../utils/dateDisplay";

import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Stack,
  Snackbar,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

function AssessmentListPage() {
  const { propertyId, unitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useAuth();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();

  const [assessments, setAssessments] = useState([]);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalAssessments, setTotalAssessments] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const isUnit = Boolean(unitId);
  const userRoles = user?.claims_role || [];
  const canCreate = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("POST")
  );
  const canUpdate = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("PUT")
  );
  const canDelete = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("DELETE")
  );

  const showActions = canDelete;

  useEffect(() => {
    const loadBreadcrumbs = async () => {
      try {
        const details = await getAssessmentParentDetails(propertyId, unitId);
        if (isUnit) {
          const propertyData = await getPropertyById(details.unit.property_id);
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: propertyData.name, path: `/properties/${details.unit.property_id}/details` },
            {
              label: `Unit ${details.unit.unit_name || details.unit.unit_code || details.unit.id}`,
              path: `/units/${unitId}/details`,
            },
            { label: "Assessments", path: null },
          ]);
        } else {
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: details.name, path: `/properties/${propertyId}/details` },
            { label: "Assessments", path: null },
          ]);
        }
      } catch (err) {
        console.error("Assessment breadcrumb load error:", err);
      }
    };

    loadBreadcrumbs();
    return () => clearCustomPath();
  }, [propertyId, unitId, isUnit, setCustomPath, clearCustomPath]);

  useEffect(() => {
    const loadAssessments = async () => {
      try {
        setLoading(true);
        setError("");
        const [types, pageData] = await Promise.all([
          getAssessmentTypes(),
          getAssessmentsPage({
            propertyId,
            unitId,
            page,
            pageSize: rowsPerPage,
          }),
        ]);

        setAssessmentTypes(types || []);
        setAssessments(pageData.results || []);
        setTotalAssessments(pageData.total || 0);
        setTotalPages(pageData.total_pages || 1);
        if (pageData.page && pageData.page !== page) {
          setPage(pageData.page);
        }
      } catch (err) {
        console.error("Assessment load error:", err);
        setError("Failed to load assessments.");
      } finally {
        setLoading(false);
      }
    };

    loadAssessments();
  }, [page, rowsPerPage, propertyId, unitId, reloadKey]);

  useEffect(() => {
    const incomingToast = location.state?.toast;
    if (!incomingToast?.message) return;
    showToast(incomingToast.message, incomingToast.severity || "success");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this assessment?")) return;

    try {
      await deleteAssessment(id);
      const shouldStepBack = page > 1 && assessments.length === 1;
      if (shouldStepBack) {
        setPage((prev) => prev - 1);
      } else {
        setReloadKey((prev) => prev + 1);
      }
      showToast("Assessment deleted");
    } catch {
      showToast("Failed to delete assessment.", "error");
    }
  };

  const formatDate = formatDateUk;

  const getTypeName = (typeId) => {
    const type = assessmentTypes.find((t) => t.id === typeId);
    return type ? type.name : `Type ${typeId}`;
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  // -----------------------------
  // RENDER
  // -----------------------------
  if (loading)
    return (
      <Layout>
        <div className="page-container">
          <p>Loading assessments...</p>
        </div>
      </Layout>
    );

  return (
    <Layout>
      <Box className="page-container">
        <Paper sx={{ p: 3 }} elevation={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h5" fontWeight="600">
              {isUnit ? "Unit Assessments" : "Property Assessments"}
            </Typography>

            {canCreate && (
              <Button
                variant="contained"
                onClick={() =>
                  navigate(
                    isUnit
                      ? `/units/${unitId}/assessments/create`
                      : `/properties/${propertyId}/assessments/create`
                  )
                }
              >
                Add Assessment
              </Button>
            )}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {totalAssessments === 0 ? (
            <Typography>No assessments found.</Typography>
          ) : (
            <>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {assessments.length} of {totalAssessments} assessments
              </Typography>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Rows per page</InputLabel>
                <Select
                  value={rowsPerPage}
                  label="Rows per page"
                  onChange={handleRowsPerPageChange}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Assessment Type</TableCell>
                    <TableCell>Pass</TableCell>
                    <TableCell>Assessment Date</TableCell>
                    <TableCell>Expiry Date</TableCell>
                    {showActions && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {assessments.map((a) => (
                    <TableRow
                      key={a.id}
                      hover={canUpdate}
                      onClick={() => {
                        if (canUpdate) {
                          navigate(
                            isUnit
                              ? `/units/${unitId}/assessments/${a.id}/edit`
                              : `/properties/${propertyId}/assessments/${a.id}/edit`
                          );
                        }
                      }}
                      sx={{ cursor: canUpdate ? "pointer" : "default" }}
                    >
                      <TableCell>{getTypeName(a.assessment_type_id)}</TableCell>
                      <TableCell>{a.passed ? "Yes" : "No"}</TableCell>
                      <TableCell>{formatDate(a.assessment_date)}</TableCell>
                      <TableCell>{formatDate(a.expiry_date)}</TableCell>

                      {showActions && (
                        <TableCell align="right">
                          {canDelete && (
                            <IconButton
                              color="error"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDelete(a.id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <Stack alignItems="center" sx={{ mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size="small"
                />
              </Stack>
            )}
            </>
          )}
        </Paper>
      </Box>

      {/* TOAST / SNACKBAR */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default AssessmentListPage;
